import ClearButton from "molecules/buttons/clear-button";
import React, { PropsWithChildren } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import BookviewSearchPanelNoResults from "./bookview-search-panel-no-results";
import BookviewTabPanelFooter from "../bookview-tab-panel-footer";
import CompactPageNavigationMenu from "molecules/page-navigation/compact-page-navigation-menu";
import { t } from "utilities/localization-utils";
import Anchor from "atoms/anchors/anchor";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import { useBookviewSearchContext } from "utilities/contexts/bookview-tab-panel/bookview-search-provider";
import HitRecord from "models/view-models/search/hit-record";
import Loader from "atoms/loaders/loader";
import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { useAtomValue } from "jotai";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import CurrentPublicationEntityAtom from "utilities/atoms/current-publication-entity-atom";
import { BookviewSearchScope } from "organisms/panels/bookview-tab-panels/bookview-search/bookview-search-scope";
import EnhancedContentBadge from "molecules/badges/enhanced-content-badge";
import { PublicationEntityTypeConstants } from "constants/publication-entity-type-constants";
import NoResultsBanner from "molecules/banners/no-results-banner";
import { StringUtils } from "@rsm-hcd/javascript-core";

const BASE_CLASS_NAME = "c-bookview-search-panel";
interface BookviewSearchPanelProps extends PropsWithChildren {
    isInLegacyPublicationView?: boolean;
}
const BookviewSearchPanel: React.FC<BookviewSearchPanelProps> = (props) => {
    const {
        searchResults,
        searchText: searchTerm,
        clearSearch,
        loading,
        totalResults,
        take,
        currentPage,
        onSetPage,
        numberOfPages,
        setIsSearchCollapsed,
        searchScope,
        setSearchScope,
    } = useBookviewSearchContext();
    const hasResults = CollectionUtils.hasValues(searchResults);
    const isPaginated = hasResults && totalResults > take;
    const { inMobileView: showMobileNav } = useBreakpointRanges();

    const showInitialState = StringUtils.isEmpty(searchTerm) && !loading;
    const showResults = hasResults && !loading;
    const showNoResults =
        !hasResults && !loading && StringUtils.hasValue(searchTerm);

    const handleClick = () => {
        if (showMobileNav) setIsSearchCollapsed(true);
    };

    const publicationEntity = useAtomValue(CurrentPublicationEntityAtom);
    const { publication } = useCurrentPublication();

    const handleCurrentViewClick = () => {
        setSearchScope(BookviewSearchScope.CurrentView);
    };
    const handleEntirePublicationClick = () => {
        setSearchScope(BookviewSearchScope.EntirePublication);
    };

    const getResultLink = (hit: HitRecord) => {
        return hit.getRoute();
    };

    const getEntityTranslation = (lower = false) => {
        let entity = t("chapter");

        switch (publicationEntity?.type) {
            case "Annex":
                entity = t("annex");
                break;
            case "Article":
                entity = t("article");
                break;
            case "Chapter":
                entity = t("chapter");
                break;
            default:
                entity = t("chapter");
                break;
        }

        return lower ? entity.toLowerCase() : entity;
    };
    return (
        <div className={BASE_CLASS_NAME}>
            <div className={`${BASE_CLASS_NAME}__header`}>
                {!props.isInLegacyPublicationView && (
                    <div className={`${BASE_CLASS_NAME}__header__options`}>
                        <Button
                            cssClassName={`${BASE_CLASS_NAME}__caaps-button ${searchScope === BookviewSearchScope.CurrentView ? "-selected" : ""}`}
                            style={ButtonStyles.Secondary}
                            size={ButtonSizes.Small}
                            onClick={handleCurrentViewClick}>
                            {t("thisItem", { item: getEntityTranslation() })}
                        </Button>
                        <Button
                            cssClassName={`${BASE_CLASS_NAME}__publication-button ${searchScope === BookviewSearchScope.EntirePublication ? "-selected" : ""}`}
                            style={ButtonStyles.Secondary}
                            size={ButtonSizes.Small}
                            onClick={handleEntirePublicationClick}>
                            {t("allOfItem", {
                                item: publication.getDisplayCodeAndEdition(),
                            })}
                        </Button>
                    </div>
                )}
                <div className={`${BASE_CLASS_NAME}__sub-header`}>
                    <span className="-label">
                        {showResults &&
                            (isPaginated ? (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            "showing-number-of-total-matches",
                                            {
                                                number: searchResults.length,
                                                total: totalResults,
                                            }
                                        ),
                                    }}
                                />
                            ) : (
                                <>Found {totalResults} matches</>
                            ))}
                    </span>
                    <ClearButton disabled={!hasResults} onClick={clearSearch} />
                </div>
            </div>

            <div className={`${BASE_CLASS_NAME}__body`}>
                {showInitialState && (
                    <NoResultsBanner
                        subtitle={t("initial-search-message", {
                            entity: getEntityTranslation(true),
                        })}
                    />
                )}

                {showResults &&
                    searchResults.map((hit, index) => (
                        <div
                            key={`${hit.id}-${index}`}
                            className={`${BASE_CLASS_NAME}__item`}>
                            <div className={`${BASE_CLASS_NAME}__item__header`}>
                                <Anchor
                                    to={getResultLink(hit)}
                                    onClick={handleClick}
                                    cssClassName={`${BASE_CLASS_NAME}__item__title`}>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: hit.getTitle() ?? "",
                                        }}></span>
                                </Anchor>
                                {hit.entityType ===
                                    PublicationEntityTypeConstants.EnhancedContent && (
                                    <EnhancedContentBadge />
                                )}
                            </div>
                            <div className={`${BASE_CLASS_NAME}__item__body`}>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: hit.body ?? "",
                                    }}></span>
                            </div>
                            <div className={`${BASE_CLASS_NAME}__item__footer`}>
                                {hit.isEnhancedContent()
                                    ? hit.enhancedContent?.getBreadcrumbText()
                                    : hit.getBreadcrumbText()}
                            </div>
                        </div>
                    ))}

                {showNoResults && (
                    <BookviewSearchPanelNoResults
                        isInLegacyPublicationView={
                            props.isInLegacyPublicationView
                        }
                        onClear={clearSearch}
                    />
                )}

                {loading && <Loader />}
            </div>
            {props.children}
            {!showMobileNav && (
                <div className={`${BASE_CLASS_NAME}__footer`}>
                    <BookviewTabPanelFooter
                        isInLegacyPublicationView={
                            props.isInLegacyPublicationView
                        }>
                        {isPaginated && (
                            <CompactPageNavigationMenu
                                currentPageNumber={currentPage}
                                setCurrentPageNumber={onSetPage}
                                onFooterPageChange={onSetPage}
                                totalPages={numberOfPages}
                            />
                        )}
                    </BookviewTabPanelFooter>
                </div>
            )}
        </div>
    );
};
export default BookviewSearchPanel;
